import { productUnits } from '@eneco/enumerations/productUnits';

import { type CostsType, type ProductFormatter } from './types';
import type { Details } from '../../../../shared/components/PriceDetails/PriceDetails';
import type { CostDetails, Offer } from '../../../../shared/types';
import { formatDateToDutch, getDisplayYear } from '../price-details-overview.helpers';

type RedeliveryFormatter = Pick<ProductFormatter, 'currency' | 'unit'>;

interface ElectricityCostDetailsTextuals {
  redeliveryLabel?: { value?: string };
  redeliveryCostLabel?: { value?: string };
  redeliveryTariffLabel?: { value?: string };
  redeliveryDynamicPricingProductTariffLabel?: { value?: string };
  redeliveryCompensationEndDateText?: { value?: string };
  redeliveryCompensationRateText?: { value?: string };
  redeliveryCostsEndDateText?: { value?: string };
  redeliveryCostsRateText?: { value?: string };
}

export interface RedeliveryBuilderContext {
  offer?: Offer;
  costDetails?: CostDetails;
  costsType: CostsType;
  discountType?: string;
  futureRate?: Exclude<Offer['productFutureRate'], undefined>;
  shouldShowDynamicPricingLabels: boolean;
  electricityCostDetails?: ElectricityCostDetailsTextuals;
  formatter: RedeliveryFormatter;
}

const getRedeliveryTariffLabel = (
  electricityCostDetails: ElectricityCostDetailsTextuals | undefined,
  shouldShowDynamicPricingLabels: boolean,
): string | undefined =>
  electricityCostDetails?.[
    shouldShowDynamicPricingLabels ? 'redeliveryDynamicPricingProductTariffLabel' : 'redeliveryTariffLabel'
  ]?.value;

const getElectricityCostDetailText = (
  electricityCostDetails: ElectricityCostDetailsTextuals | undefined,
  key: keyof ElectricityCostDetailsTextuals,
): string | undefined => electricityCostDetails?.[key]?.value;

export const buildRedeliveryDetailsCPQ = (
  {
    offer,
    costDetails,
    costsType,
    discountType,
    futureRate,
    shouldShowDynamicPricingLabels,
    electricityCostDetails,
    formatter: { currency, unit },
  }: RedeliveryBuilderContext,
  electricityRedeliveryRemainder: number,
  solarPanelsOutput: number,
): Details[] => {
  const redeliveryDetails: Details[] = [];

  if (Number(costDetails?.electricityTotal?.redeliveryCost?.[costsType]) !== 0) {
    redeliveryDetails.push(
      {
        text: `${electricityCostDetails?.redeliveryLabel?.value} ${getDisplayYear(offer, discountType)}`,
        highlightText: true,
      },
      {
        title: `${electricityCostDetails?.redeliveryCostLabel?.value} ${getDisplayYear(offer, discountType)}`,
        text: `${unit.electricity((solarPanelsOutput ?? 0) - (costDetails?.electricityFutureCostDetails?.redeliveryCost?.usage ?? 0))}`,
        value: `${currency.euroHighPrecision(costDetails?.electricityCostDetails?.redeliveryCostTariff?.price?.[costsType])}/${productUnits.electricity}`,
      },
      {
        text: `${currency.euroFiveDecimals(
          costDetails?.electricityCostDetails?.redeliveryCostTariff?.price?.[costsType],
        )} × ${unit.electricity((solarPanelsOutput ?? 0) - (costDetails?.electricityFutureCostDetails?.redeliveryCost?.usage ?? 0))}`,
        value: costDetails?.electricityTotal?.redeliveryCost?.[costsType],
      },
    );
  }

  if (electricityRedeliveryRemainder !== 0) {
    redeliveryDetails.push(
      {
        title: `${getRedeliveryTariffLabel(electricityCostDetails, shouldShowDynamicPricingLabels)} ${getDisplayYear(offer, discountType)}`,
        text: `${unit.electricity(electricityRedeliveryRemainder)}`,
        value: `${currency.euroHighPrecision(costDetails?.electricityCostDetails?.redeliverytariff?.price?.vatExcluded)}/${productUnits.electricity}`,
      },
      {
        text: `${currency.euroFiveDecimals(
          costDetails?.electricityCostDetails?.redeliverytariff?.price?.vatExcluded,
        )} × ${unit.electricity(electricityRedeliveryRemainder)} = `,
        value: costDetails?.electricityTotal?.electricityRedeliveryRefund?.vatExcluded,
      },
    );
  }

  if (
    Number(costDetails?.electricityFutureCostDetails?.redeliveryCost?.[costsType]) !== 0 &&
    (costDetails?.electricityFutureCostDetails?.redeliveryCost?.usage ?? 0) > 0
  ) {
    redeliveryDetails.push(
      {
        text: `${electricityCostDetails?.redeliveryLabel?.value} ${getDisplayYear(offer, discountType, true)}`,
        highlightText: true,
      },
      {
        title: `${electricityCostDetails?.redeliveryCostLabel?.value} ${getDisplayYear(offer, discountType, true)}`,
        text: `${unit.electricity(costDetails?.electricityFutureCostDetails?.redeliveryCost?.usage)}`,
        value: `${currency.euroHighPrecision(futureRate?.zonOpDak?.vatIncluded ?? 0)}/${productUnits.electricity}`,
      },
      {
        text: `${currency.euroFiveDecimals(
          futureRate?.zonOpDak?.vatIncluded ?? 0,
        )} × ${unit.electricity(costDetails?.electricityFutureCostDetails?.redeliveryCost?.usage)} = `,
        value: costDetails?.electricityFutureCostDetails?.redeliveryCost?.price?.[costsType],
      },
    );
  }

  if ((costDetails?.electricityFutureCostDetails?.electricityRedeliveryRefund?.usage ?? 0) > 0) {
    redeliveryDetails.push(
      {
        title: `${getRedeliveryTariffLabel(electricityCostDetails, shouldShowDynamicPricingLabels)} ${getDisplayYear(offer, discountType, true)}`,
        text: `${unit.electricity(costDetails?.electricityFutureCostDetails?.electricityRedeliveryRefund?.usage ?? 0)}`,
        value: `${currency.euroHighPrecision(futureRate?.electricityRedeliveryRefund?.vatExcluded ?? 0)}/${productUnits.electricity}`,
      },
      {
        text: `${currency.euroFiveDecimals(
          futureRate?.electricityRedeliveryRefund?.vatExcluded ?? 0,
        )} × ${unit.electricity(costDetails?.electricityFutureCostDetails?.electricityRedeliveryRefund?.usage ?? 0)} = `,
        value: costDetails?.electricityFutureCostDetails?.electricityRedeliveryRefund?.price?.[costsType],
      },
    );
  }

  return redeliveryDetails;
};

export const buildRedeliveryDetails = (
  {
    offer,
    costDetails,
    costsType,
    futureRate,
    shouldShowDynamicPricingLabels,
    electricityCostDetails,
    formatter: { currency, unit },
  }: RedeliveryBuilderContext,
  electricityRedeliveryRemainder: number,
  solarPanelsOutput: number,
): Details[] => {
  const redeliveryDetails: Details[] = [];

  if (Number(costDetails?.electricityTotal?.redeliveryCost?.[costsType]) !== 0) {
    redeliveryDetails.push(
      {
        text: electricityCostDetails?.redeliveryCostLabel?.value,
        value: `${currency.euroHighPrecision(costDetails?.electricityCostDetails?.redeliveryCostTariff?.price?.[costsType])}/${productUnits.electricity}`,
      },
      {
        text: `${currency.euroFiveDecimals(
          costDetails?.electricityCostDetails?.redeliveryCostTariff?.price?.[costsType],
        )} × ${unit.electricity(solarPanelsOutput ?? 0)} = `,
        value: costDetails?.electricityTotal?.redeliveryCost?.[costsType],
      },
    );
  }

  if (electricityRedeliveryRemainder !== 0) {
    redeliveryDetails.push(
      {
        text: getRedeliveryTariffLabel(electricityCostDetails, shouldShowDynamicPricingLabels),
        value: `${currency.euroHighPrecision(costDetails?.electricityCostDetails?.redeliverytariff?.price?.vatExcluded)}/${productUnits.electricity}`,
      },
      {
        text: `${currency.euroFiveDecimals(
          costDetails?.electricityCostDetails?.redeliverytariff?.price?.vatExcluded,
        )} × ${unit.electricity(electricityRedeliveryRemainder ?? 0)} = `,
        value: costDetails?.electricityTotal?.electricityRedeliveryRefund?.vatExcluded,
      },
    );
  }

  if (futureRate) {
    const zonOpDakBeginDate = futureRate?.zonOpDak?.beginDate ? formatDateToDutch(futureRate.zonOpDak.beginDate) : null;
    const redeliveryRefundBeginDate = futureRate?.electricityRedeliveryRefund?.beginDate
      ? formatDateToDutch(futureRate.electricityRedeliveryRefund.beginDate)
      : null;

    if (futureRate?.electricityRedeliveryRefund?.vatExcluded !== undefined && zonOpDakBeginDate) {
      redeliveryDetails.push(
        {
          text: getElectricityCostDetailText(electricityCostDetails, 'redeliveryCompensationEndDateText')?.replace(
            '{date}',
            zonOpDakBeginDate,
          ),
          value: `${currency.euroHighPrecision(offer?.costDetails?.electricityCostDetails?.redeliverytariff?.price?.vatIncluded ?? 0)}/${productUnits.electricity}`,
        },
        {
          text: getElectricityCostDetailText(electricityCostDetails, 'redeliveryCompensationRateText')?.replace(
            '{date}',
            zonOpDakBeginDate,
          ),
          value: `${currency.euroHighPrecision(futureRate?.electricityRedeliveryRefund?.vatExcluded ?? 0)}/${productUnits.electricity}`,
        },
      );
    }

    if (futureRate?.zonOpDak?.vatIncluded !== undefined && redeliveryRefundBeginDate) {
      redeliveryDetails.push(
        {
          text: getElectricityCostDetailText(electricityCostDetails, 'redeliveryCostsEndDateText')?.replace(
            '{date}',
            redeliveryRefundBeginDate,
          ),
          value: `${currency.euroHighPrecision(offer?.costDetails?.electricityCostDetails?.redeliveryCostTariff?.price?.vatIncluded ?? 0)}/${productUnits.electricity}`,
        },
        {
          text: getElectricityCostDetailText(electricityCostDetails, 'redeliveryCostsRateText')?.replace(
            '{date}',
            redeliveryRefundBeginDate,
          ),
          value: `${currency.euroHighPrecision(futureRate?.zonOpDak?.vatIncluded ?? 0)}/${productUnits.electricity}`,
        },
      );
    }
  }

  if ((costDetails?.electricityTotal?.salesFee?.vatIncluded ?? 0) > 0) {
    redeliveryDetails.push(
      {
        text: 'Verkoopvergoeding',
        value: `${currency.euroHighPrecision(costDetails?.electricityCostDetails?.salesFee?.price?.[costsType] ?? 0)}/${productUnits.electricity}`,
      },
      {
        text: `${currency.euroFiveDecimals(
          costDetails?.electricityCostDetails?.salesFee?.price?.[costsType] ?? 0,
        )} × ${unit.electricity(costDetails?.electricityTotal?.salesFee?.usage ?? 0)} = `,
        value: costDetails?.electricityTotal?.salesFee?.[costsType],
      },
    );
  }

  return redeliveryDetails;
};

export const getRedeliveryDetailsForOffer = (
  context: RedeliveryBuilderContext,
  hasCPQResponse: boolean,
  electricityRedeliveryRemainder: number,
  solarPanelsOutput: number,
): Details[] =>
  hasCPQResponse
    ? buildRedeliveryDetailsCPQ(context, electricityRedeliveryRemainder, solarPanelsOutput)
    : buildRedeliveryDetails(context, electricityRedeliveryRemainder, solarPanelsOutput);
